import { useState } from 'react';
import { useParams } from 'react-router';
import { toast } from 'react-toastify';
import {
  IPaymentPlanInstalmentItem,
  IPaymentPlanTerms,
  PaymentPlanFirstPaymentDate,
  PaymentPlanFrequency,
  PaymentPlanPreview,
} from 'shared';

import { useCreatePaymentPlanMutation } from '../../../services/api/invoiceApi/invoice';
import { CreatePaymentPlanModal } from '../../../../common/Modals/CreatePaymentPlanModal';
import { useApiRequest } from '../../../hooks/useApiRequest';

interface InvoicePaymentPlanModalProps {
  open: boolean;
  onClose: () => void;
  /** Defaults to the invoice in the route */
  organisationId?: string;
  invoiceId?: string;
  /** Overrides on the default terms, e.g. from a click to call summary */
  initialTerms?: Partial<IPaymentPlanTerms>;
  /** Links the plan to the click to call it was suggested from */
  contactCallId?: string;
  onCreated?: () => void;
}

/** Create payment plan modal for an invoice, with preview and terms state */
export function InvoicePaymentPlanModal({ open, onClose, initialTerms, contactCallId, onCreated, ...props }: InvoicePaymentPlanModalProps) {
  const params = useParams();
  const organisationId = props.organisationId || params.organisationId;
  const invoiceId = props.invoiceId || params.invoiceId;
  const [createPaymentPlan, { isLoading: createPaymentPlanLoading }] = useCreatePaymentPlanMutation();
  const apiRequest = useApiRequest();
  const [terms, setTerms] = useState<IPaymentPlanTerms>({
    frequencyType: PaymentPlanFrequency.MONTHLY,
    numberOfPayments: 4,
    firstPaymentDate: PaymentPlanFirstPaymentDate.Now,
    surchargeCents: 100,
    surchargePercent: 5,
    surchargeUnit: null,
    ...initialTerms,
  });
  const [paymentPlanPreview, setPaymentPlanPreview] = useState<PaymentPlanPreview | null>(null);
  const [adjustedInstalments, setAdjustedInstalments] = useState<IPaymentPlanInstalmentItem[]>([]);
  const [paymentPlanPreviewError, setPaymentPlanPreviewError] = useState<string | null>(null);
  const [paymentPlanPreviewLoading, setPaymentPlanPreviewLoading] = useState(false);

  async function onPaymentPlanPreview() {
    setPaymentPlanPreviewLoading(true);
    setPaymentPlanPreviewError(null);
    try {
      const result = await apiRequest.post(`/invoice/${invoiceId}/payment-plan-preview`, {
        terms,
      });

      setPaymentPlanPreview(result.data);
      setAdjustedInstalments(result.data?.instalments || []);
      setPaymentPlanPreviewLoading(false);
    }
    catch (err) {
      console.error(err);
      setPaymentPlanPreviewLoading(false);
      setPaymentPlanPreview(null);
      setAdjustedInstalments([]);

      const message = err.response?.data?.toString() || `Could not fetch payment plan preview`;

      setPaymentPlanPreviewError(message);
      toast.error(message);
    }
  }

  function onPaymentPlanClose() {
    onClose();
    setPaymentPlanPreview(null);
    setAdjustedInstalments([]);
    setPaymentPlanPreviewError(null);
  }

  async function onConfirmPaymentPlan() {
    try {
      await createPaymentPlan({
        organisationId,
        invoiceId,
        terms,
        adjustedInstalments,
        contactCallId,
      }).unwrap();
    }
    catch (err) {
      toast.error(err?.data?.message || `Could not create the payment plan`);

      return;
    }

    onClose();
    onCreated?.();
  }

  function setTermsAndClearPreview(next: IPaymentPlanTerms) {
    // Clear preview whenever options change
    if (paymentPlanPreview) {
      setPaymentPlanPreview(null);
      setAdjustedInstalments([]);
    }
    if (paymentPlanPreviewError) {
      setPaymentPlanPreviewError(null);
    }

    setTerms(next);
  }

  return (
    <CreatePaymentPlanModal
      mode={ `app` }
      isLoading={ createPaymentPlanLoading }
      isOpen={ open }
      onClose={ onPaymentPlanClose }
      terms={ terms }
      setTerms={ setTermsAndClearPreview }
      onCreate={ onConfirmPaymentPlan }
      onPreview={ onPaymentPlanPreview }
      preview={ paymentPlanPreview }
      adjustedInstalments={ adjustedInstalments }
      onAdjustedInstalmentsChange={ setAdjustedInstalments }
      previewError={ paymentPlanPreviewError }
      previewLoading={ paymentPlanPreviewLoading }
    />
  );
}
